import React, { useState, useEffect } from 'react';
import { notificationService, NotificationTypeInfo } from '../services/NotificationService';
import { auth } from '../firebase';
import './ParentNotificationsView.css';

function ParentNotificationsView() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (auth.currentUser?.uid) {
      loadNotifications(auth.currentUser.uid);
    }
  }, []);

  const loadNotifications = (parentId) => {
    setLoading(true);
    notificationService.onNotifications(parentId, (notifications) => {
      setNotifications(notifications);
      setUnreadCount(notifications.filter(n => !n.isRead).length);
      setLoading(false);
    });
  };

  const markAsRead = async (notification) => {
    if (!notification.isRead) {
      await notificationService.markAsRead(notification.id);
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    if (auth.currentUser?.uid) {
      await notificationService.markAllAsRead(auth.currentUser.uid);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    }
  };

  const deleteNotification = async (notificationId) => {
    await notificationService.deleteNotification(notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    const deleted = notifications.find(n => n.id === notificationId);
    if (deleted && !deleted.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const getIconInfo = (type) => {
    const info = NotificationTypeInfo[type];
    if (info) return info;
    return { icon: 'ℹ️', color: '#6b7280' };
  };

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return 'Unknown';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="parent-notifications-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-notifications-view">
      <div className="notifications-header">
        <h2>Notifications</h2>
        {unreadCount > 0 && (
          <button className="mark-all-read-btn" onClick={markAllAsRead}>
            Mark All Read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔕</div>
          <h3>No Notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map(notification => {
            const { icon, color } = getIconInfo(notification.type);
            return (
              <div
                key={notification.id}
                className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                onClick={() => markAsRead(notification)}
              >
                <div className="notification-icon" style={{ backgroundColor: `${color}20`, color }}>
                  {icon}
                </div>
                <div className="notification-content">
                  <h4>{notification.title}</h4>
                  <p>{notification.message}</p>
                  <span className="notification-time">{formatRelativeTime(notification.createdAt)}</span>
                </div>
                <div className="notification-actions">
                  {!notification.isRead && <div className="unread-dot"></div>}
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ParentNotificationsView;
